import { getBillingHoldMaxDate } from '@ourbranch/policy-utils';
import { getTodayStr } from '@ourbranch/date-fns-helpers';

/**
 * Front-end billing-hold ("Place on Hold") brand helpers (#33568 / #33624).
 *
 * Mirrors the backend `Brand.getBillingHoldConfig()` and the `getRateControlDateConfig` FE pattern.
 * Branch keeps its existing billing-hold behavior (8-day-ish installment window, no reason, no
 * "hold cancellations" option); the mutual carriers enable the enhanced feature (15-day cap, required
 * reason, and the "hold cancellations as well" option).
 */

export const DEFAULT_BILLING_HOLD_CONFIG = {
  maxDays: 8,
  requireReason: false,
  holdCancellationsEnabled: false
};

/** The brand's billingHold config, defaulting to Branch behavior (8 / false / false). */
export function getBillingHoldConfig(brandConfig) {
  return brandConfig?.billingHold ?? DEFAULT_BILLING_HOLD_CONFIG;
}

/**
 * True for brands running the enhanced Place-on-Hold feature (the mutuals). Used to gate the reason
 * field, the "hold cancellations as well" checkbox, AND the flat `today + maxDays` date cap — so Branch
 * keeps its existing (more permissive) staff hold-date logic untouched.
 */
export function usesEnhancedBillingHold(config) {
  return Boolean(config.requireReason || config.holdCancellationsEnabled);
}

/**
 * True when the staffer is actively placing or changing a hold in THIS edit (an active future hold
 * whose date/reason/cancellations differ from the saved policy). Gates the "reason required" rule so a
 * pre-existing reasonless hold does not block saving unrelated Payment-tab fields on load.
 */
export function isPlacingBillingHold({ values, savedPolicy }) {
  const holdIsActive =
    !!values.billingHoldUntil && new Date(values.billingHoldUntil) > new Date(savedPolicy.effectiveDate);
  const holdChanged =
    values.billingHoldUntil !== savedPolicy.billingHoldUntil ||
    (values.billingHoldReason ?? '') !== (savedPolicy.billingHoldReason ?? '') ||
    (values.billingHoldBlocksCancellations ?? false) !== (savedPolicy.billingHoldBlocksCancellations ?? false);
  return holdIsActive && holdChanged;
}

/**
 * The latest date a hold may be set to for an enhanced (mutual) brand: `today + maxDays` in the
 * policy's state timezone, never past the policy's `sevenDaysBeforeEndDate` guard. Returns a Date for
 * the date-picker `maxDate`. Branch does not use this — it keeps its existing cap.
 */
export function getEnhancedMaxBillingHoldDate({ config, state, sevenDaysBeforeEndDate }) {
  const capStr = getBillingHoldMaxDate(getTodayStr(state), config.maxDays);
  const capDate = new Date(`${capStr}T00:00:00`);
  return capDate < sevenDaysBeforeEndDate ? capDate : sevenDaysBeforeEndDate;
}
